import { computed, ref } from "@vue/composition-api";
import {
  PageableSearchInput,
  PageableSearchResult,
  SearchResult,
  SortableSearch,
} from "@scrile/api-provider/dist/api/types";

interface PageableState<T> extends PageableSearchResult, SearchResult<T>, SortableSearch {}

export default function usePager<T, D extends PageableSearchInput>(
  loader: (page: number, requestData?: D) => Promise<PageableState<T>>,
  onGetData?: (response: T[]) => void
) {
  const enableScroll = ref(false);
  const data = ref<PageableState<T>>();
  const isLastPage = computed(() => data.value && !data.value.hasNextPage);

  const items = computed(() => {
    return data.value?.result ?? [];
  });

  const getData = async (page: number = 0, requestData?: D, replace?: boolean) => {
    try {
      enableScroll.value = false;
      const response = await loader(page, requestData);
      data.value = {
        ...response,
        result: !data.value || replace ? response.result : data.value.result.concat(response.result),
      };
      onGetData && onGetData(response.result);
    } finally {
      enableScroll.value = data.value?.hasNextPage ?? false;
    }
  };

  const getNextPage = async (cb?: Function, requestData?: D) => {
    if (!enableScroll.value) {
      return;
    }
    const nextPage = (data.value?.page ?? 0) + 1;
    await getData(nextPage, requestData, false);
    cb && cb();
  };

  const getFirstPage = (requestData?: D) => {
    return getData(0, requestData, true);
  };

  const getLostItem = async (checkAllowUpdate?: (data: PageableState<T>) => boolean, requestData?: Partial<D>) => {
    if (!data.value || !data.value.hasNextPage) return;
    try {
      enableScroll.value = false;
      const response = await loader(data.value.result.length, { size: 1, ...((requestData ?? {}) as D) });

      const allowUpdate = checkAllowUpdate ? checkAllowUpdate(response) : true;
      if (!allowUpdate) return;

      data.value = {
        ...data.value,
        totalRecords: response.totalRecords,
        hasNextPage: response.hasNextPage,
        result: data.value.result.concat(response.result),
      };
    } finally {
      enableScroll.value = data.value.hasNextPage;
    }
  };

  const getLostItems = async (size = 1) => {
    if (size <= 0) return;
    if (size === 1) return getLostItem();

    if (!data.value || !data.value.hasNextPage) return;
    try {
      enableScroll.value = false;
      const page = data.value.page - Math.floor(size / data.value.size);
      const response = await loader(page);
      const lostItems = response.result.slice(-(size % data.value.size));
      data.value = {
        ...response,
        result: data.value.result.concat(lostItems),
      };
    } finally {
      enableScroll.value = data.value.hasNextPage;
    }
  };

  const removeItem = async (comparator: (item: T) => boolean) => {
    if (!data.value) return;
    const filteredList = data.value.result.filter(i => !comparator(i));
    const removedCount = data.value.result.length - filteredList.length;
    data.value = {
      ...data.value,
      result: filteredList,
      totalRecords: data.value.totalRecords - removedCount,
    };
    await getLostItems(removedCount);
  };

  const updateItem = (newItem: T, comparator: (i1: T, i2: T) => boolean) => {
    if (!data.value) return;
    data.value = {
      ...data.value,
      result: data.value.result.map(i => (comparator(i, newItem) ? newItem : i)),
    };
  };
  /*
     Только для добавления элемента в начало списка.
     Если тебе нужно добавить элемент в список, который при повторном вызове loader не будет на том же месте в списке,
     подумай, возможно это не тот метод.
   */
  const addItems = (newItems: T[]) => {
    if (!data.value) return;
    data.value = {
      ...data.value,
      result: newItems.concat(data.value.result),
      totalRecords: data.value.totalRecords + newItems.length,
    };
    //todo: add support for prevPage
    if (data.value.hasNextPage) {
      data.value.result.splice(-newItems.length, newItems.length);
    }
  };

  const addItem = (newItem: T) => {
    addItems([newItem]);
  };

  return {
    enableScroll,
    data,
    items,
    isLastPage,
    getFirstPage,
    getNextPage,
    removeItem,
    updateItem,
    addItem,
    addItems,
    getLostItem,
    getLostItems,
  };
}
